import React, { useState, useEffect } from 'react';
import { Layout as AntLayout, Menu, Dropdown, Avatar, Space, Button, Drawer, Grid } from 'antd';
import {
  DashboardOutlined,
  MobileOutlined,
  SecurityScanOutlined,
  FolderOutlined,
  AppstoreOutlined,
  CloudUploadOutlined,
  UserOutlined,
  LogoutOutlined,
  LockOutlined,
  DeleteOutlined,
  MenuOutlined,
  CloseOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ChangePassword from './ChangePassword';

const { Header, Sider, Content, Footer } = AntLayout;
const { useBreakpoint } = Grid;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, refreshUser } = useAuth();
  const [changePasswordVisible, setChangePasswordVisible] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const screens = useBreakpoint();
  
  // 判断是否为移动端
  const isMobile = !screens.md;

  // 检测用户类型，如果为空则尝试刷新
  useEffect(() => {
    if (user && !user.userType) {
      console.log('Detected missing userType, refreshing user info...');
      refreshUser().then(success => {
        if (success) {
          console.log('User info refreshed successfully');
        } else {
          console.log('Failed to refresh user info');
        }
      });
    }
  }, [user, refreshUser]);

  // 路由变化时关闭移动端菜单
  useEffect(() => {
    setMobileMenuVisible(false);
  }, [location.pathname]);

  const menuItems = (
    user?.userType === 'device_editor'
      ? [
          {
            key: '/devices',
            icon: <MobileOutlined />,
            label: '设备列表',
          },
        ]
      : [
          {
            key: '/',
            icon: <DashboardOutlined />,
            label: '仪表盘',
          },
          {
            key: '/versions',
            icon: <AppstoreOutlined />,
            label: '版本列表',
          },
          {
            key: '/applications',
            icon: <CloudUploadOutlined />,
            label: '应用管理',
          },
          {
            key: '/policies',
            icon: <SecurityScanOutlined />,
            label: '管控措施列表',
          },
          {
            key: '/categories',
            icon: <FolderOutlined />,
            label: '目录列表',
          },
          {
            key: '/devices',
            icon: <MobileOutlined />,
            label: '设备列表',
          },
          // 只有超级管理员才能看到用户管理
          ...(user?.userType === 'super_admin' ? [{
            key: '/users',
            icon: <UserOutlined />,
            label: '用户管理',
          }] : []),
          // 只有超级管理员才能看到重置数据
          ...(user?.userType === 'super_admin' ? [{
            key: '/reset-data',
            icon: <DeleteOutlined />,
            label: '重置数据',
            danger: true,
          }] : []),
        ]
  );

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
    // 移动端点击菜单后关闭抽屉
    if (isMobile) {
      setMobileMenuVisible(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const userMenuItems = [
    {
      key: 'change-password',
      icon: <LockOutlined />,
      label: '修改密码',
      onClick: () => setChangePasswordVisible(true),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  // 菜单组件（复用于侧边栏和抽屉）
  const menuComponent = (
    <Menu
      mode="inline"
      selectedKeys={[location.pathname]}
      style={{ height: '100%', borderRight: 0 }}
      items={menuItems}
      onClick={handleMenuClick}
    />
  );

  return (
    <AntLayout className="ant-layout">
      <Header className="ant-layout-header" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: isMobile ? '0 12px' : '0 24px',
        height: isMobile ? 56 : 64
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {isMobile && (
            <MenuOutlined 
              className="mobile-menu-trigger"
              onClick={() => setMobileMenuVisible(true)}
              style={{ color: 'white', fontSize: 20, marginRight: 12 }}
            />
          )}
          <div className="logo" style={{ 
            color: 'white', 
            fontSize: isMobile ? 15 : 18, 
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: isMobile ? 280 : 'auto'
          }}>
            📱 {isMobile ? '易管MDM' : '易管移动设备管控平台'}
          </div>
        </div>
        <Space size={isMobile ? 'small' : 'middle'}>
          {!isMobile && (
            <span style={{ color: 'white' }}>欢迎，{user?.username} ({user?.userType || 'unknown'})</span>
          )}
          <Dropdown
            menu={{ items: userMenuItems }}
            placement="bottomRight"
            trigger={['click']}
          >
            <Button
              type="text"
              style={{ color: 'white', padding: isMobile ? '4px 8px' : undefined }}
              icon={<Avatar size="small" icon={<UserOutlined />} />}
            >
              {!isMobile && user?.username}
            </Button>
          </Dropdown>
        </Space>
      </Header>
      <AntLayout>
        {/* 桌面端侧边栏 */}
        {!isMobile && (
          <Sider width={200} style={{ background: '#fff' }}>
            {menuComponent}
          </Sider>
        )}
        
        {/* 移动端抽屉菜单 */}
        <Drawer
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>📱 导航菜单</span>
            </div>
          }
          placement="left"
          onClose={() => setMobileMenuVisible(false)}
          open={mobileMenuVisible}
          width={280}
          className="mobile-sider-drawer"
          styles={{ body: { padding: 0 } }}
          closeIcon={<CloseOutlined />}
        >
          <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
            <div style={{ fontSize: 14, color: '#666' }}>当前用户</div>
            <div style={{ fontSize: 16, fontWeight: 500, marginTop: 4 }}>{user?.username}</div>
            <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{user?.userType || 'unknown'}</div>
          </div>
          {menuComponent}
        </Drawer>

        <AntLayout style={{ padding: '0' }}>
          <Content className="ant-layout-content">
            {children}
          </Content>
          <Footer style={{
            textAlign: 'center',
            background: '#f0f2f5',
            borderTop: '1px solid #d9d9d9',
            padding: isMobile ? '12px 16px' : '16px 24px',
            fontSize: isMobile ? 12 : 14
          }}>
            <div>
              V2508112 © 2013-2025{' '}
              <a
                href="https://baibaomen.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1890ff', textDecoration: 'none' }}
              >
                长沙百宝门信息科技有限公司
              </a>
            </div>
            <div style={{ marginTop: '4px' }}>
              湘ICP备14000611号-1
            </div>
          </Footer>
        </AntLayout>
      </AntLayout>

      {/* 修改密码弹窗 */}
      <ChangePassword
        visible={changePasswordVisible}
        onCancel={() => setChangePasswordVisible(false)}
      />
    </AntLayout>
  );
};

export default Layout;
